import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import PageSectionNav from '../../components/feature/PageSectionNav';

export default function ResourcesPage() {
  const { t } = useTranslation();
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  const forms = [
    { 
      icon: 'ri-user-add-line', 
      title: t('resourcesPage.membershipForm'),
      description: t('resourcesPage.membershipFormDesc'),
      link: '/resources/membership-form'
    },
    { 
      icon: 'ri-water-flash-line', 
      title: t('resourcesPage.baptismForm'),
      description: t('resourcesPage.baptismFormDesc'),
      link: '/resources/baptism-form'
    },
    { 
      icon: 'ri-heart-2-line', 
      title: t('resourcesPage.engagementForm'),
      description: t('resourcesPage.engagementFormDesc'),
      link: '/resources/engagement-form'
    },
    { 
      icon: 'ri-hearts-line', 
      title: t('resourcesPage.weddingForm'),
      description: t('resourcesPage.weddingFormDesc'),
      link: '/resources/marriage-form'
    },
    { 
      icon: 'ri-candle-line', 
      title: t('resourcesPage.funeralForm'),
      description: t('resourcesPage.funeralFormDesc'),
      link: '/resources/funeral-form'
    },
    { 
      icon: 'ri-calendar-check-line', 
      title: t('resourcesPage.eventBooking'),
      description: t('resourcesPage.eventBookingDesc'),
      link: '/resources/event-booking-form'
    }
  ];

  const libraries = [
    {
      icon: 'ri-book-3-line',
      title: t('resourcesPage.popeShenoudaBooks'),
      description: t('resourcesPage.popeShenoudaBooksDesc'),
      items: [
        { label: t('resourcesPage.releaseOfSpirit'), filename: 'release-of-spirit.pdf', url: null },
        { label: t('resourcesPage.lifeOfFaith'), filename: 'life-of-repentance-and-purity.pdf', url: null },
        { label: t('resourcesPage.contemplations'), filename: 'contemplations-on-psalms.pdf', url: null },
        { label: t('resourcesPage.wordsOfBenefit'), filename: 'words-of-spiritual-benefit.pdf', url: null },
      ]
    },
    {
      icon: 'ri-music-2-line',
      title: t('resourcesPage.hymnalResources'),
      description: t('resourcesPage.hymnalResourcesDesc'),
      items: [
        { label: t('resourcesPage.copticHymnsBook'), filename: 'coptic-hymns-book.pdf', url: null },
        { label: t('resourcesPage.tasbehaLyrics'), filename: 'tasbeha-lyrics.pdf', url: null },
        { label: t('resourcesPage.liturgyResponses'), filename: 'liturgy-of-st-basil.pdf', url: null },
        { label: t('resourcesPage.seasonalHymns'), filename: 'seasonal-hymns.pdf', url: null },
      ]
    },
    {
      icon: 'ri-book-open-line',
      title: t('resourcesPage.bibleStudyMaterials'),
      description: t('resourcesPage.bibleStudyMaterialsDesc'),
      items: [
        { label: t('resourcesPage.oldTestament'), filename: 'old-testament-study.pdf', url: null },
        { label: t('resourcesPage.newTestament'), filename: 'new-testament-study.pdf', url: null },
        { label: t('resourcesPage.psalmsCommentary'), filename: 'psalms-commentary.pdf', url: null },
        { label: t('resourcesPage.churchFathers'), filename: 'church-fathers.pdf', url: null },
      ]
    },
    {
      icon: 'ri-graduation-cap-line',
      title: t('resourcesPage.sundaySchoolCurriculum'),
      description: t('resourcesPage.sundaySchoolCurriculumDesc'),
      items: [
        { label: t('resourcesPage.preKMaterials'), filename: 'pre-k-materials.pdf', url: null },
        { label: t('resourcesPage.elementaryLessons'), filename: 'elementary-lessons.pdf', url: null },
        { label: t('resourcesPage.youthStudies'), filename: 'youth-studies.pdf', url: null },
        { label: t('resourcesPage.adultFormation'), filename: 'adult-formation.pdf', url: null },
      ]
    }
  ];

  return (
    <PageLayout hasHero>
      <PageHero 
        title={t('resourcesPage.title')}
        subtitle={t('resourcesPage.subtitle')}
        backgroundImage="/assets/readdy/ef9893d6-f16e-4e1a-9c8b-f301f1bd7ed2_IMG_0494-0d688b2752c9.jpeg"
      />

      <PageSectionNav
        sections={[
          { id: 'church-forms', label: t('resourcesPage.churchForms') },
          { id: 'coptic-answers', label: t('resourcesPage.copticAnswersTitle') },
          { id: 'libraries', label: t('resourcesPage.librariesTitle') },
          { id: 'brand-assets', label: t('resourcesPage.brandAssets') },
        ]}
      />

      {/* Forms Section */}
      <section id="church-forms" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-amber-600 mb-3 uppercase tracking-wider font-medium">{t('resourcesPage.quickAccess')}</p>
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">{t('resourcesPage.churchForms')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('resourcesPage.formsDesc')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map((form, index) => (
              <Link 
                key={index}
                to={form.link}
                className="group bg-stone-50 hover:bg-white rounded-2xl p-6 border border-stone-100 hover:border-amber-200 hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-amber-100 text-amber-700 rounded-xl mb-4 group-hover:bg-amber-600 group-hover:text-white transition-colors">
                  <i className={`${form.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">{form.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{form.description}</p>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-600">
                  {t('resourcesPage.accessForm')}
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Coptic Orthodox Answers Section */}
      <section id="coptic-answers" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative w-full h-[400px] rounded-2xl overflow-hidden">
                <img 
                  src="/assets/readdy/ae786c689437b8a418379ba252b29617-a2f9dfa99b18.jpeg"
                  alt="Coptic Orthodox Answers"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
            </div>
            
            <div>
              <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-sm font-medium rounded-full mb-6">
                {t('resourcesPage.dioceseService')}
              </span>
              <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6">{t('resourcesPage.copticAnswersTitle')}</h2>
              <p className="text-lg text-gray-600 mb-6">{t('resourcesPage.copticAnswersDesc')}</p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-question-answer-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{t('resourcesPage.faithQuestions')}</h4>
                    <p className="text-sm text-gray-600">{t('resourcesPage.faithQuestionsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-book-marked-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{t('resourcesPage.theologicalTopics')}</h4>
                    <p className="text-sm text-gray-600">{t('resourcesPage.theologicalTopicsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-video-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{t('resourcesPage.videoResources')}</h4>
                    <p className="text-sm text-gray-600">{t('resourcesPage.videoResourcesDesc')}</p>
                  </div>
                </div>
              </div>

              <a 
                href="https://www.copticorthodoxanswers.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3d1a1e] text-white font-medium rounded-xl hover:bg-[#2a1215] transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('resourcesPage.visitCopticAnswers')}
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Libraries Section */}
      <section id="libraries" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-amber-600 mb-3 uppercase tracking-wider font-medium">{t('resourcesPage.learnGrow')}</p>
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">{t('resourcesPage.librariesTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('resourcesPage.librariesDesc')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {libraries.map((library, index) => (
              <div key={index} className="bg-stone-50 rounded-2xl p-8 hover:shadow-xl transition-all border border-stone-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#3d1a1e] text-white rounded-xl flex-shrink-0">
                    <i className={`${library.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{library.title}</h3>
                    <p className="text-sm text-gray-600">{library.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {library.items.map((item, idx) => (
                    item.url ? (
                      <a
                        key={idx}
                        href={item.url}
                        download={item.filename}
                        className="flex items-center gap-2 text-sm text-gray-700 hover:text-amber-600 transition-colors cursor-pointer group"
                      >
                        <i className="ri-file-pdf-2-line text-red-500 flex-shrink-0"></i>
                        <span className="group-hover:underline truncate">{item.label}</span>
                        <i className="ri-download-line text-xs text-gray-400 group-hover:text-amber-500 flex-shrink-0 ml-auto"></i>
                      </a>
                    ) : (
                      <span
                        key={idx}
                        className="flex items-center gap-2 text-sm text-gray-400 cursor-not-allowed group"
                        title="Upload file to enable download"
                      >
                        <i className="ri-file-pdf-2-line text-gray-300 flex-shrink-0"></i>
                        <span className="truncate">{item.label}</span>
                        <i className="ri-upload-cloud-2-line text-xs text-gray-300 flex-shrink-0 ml-auto"></i>
                      </span>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Church Logo & Coptic Font Section */}
      <section id="brand-assets" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-amber-600 mb-3 uppercase tracking-wider font-medium">{t('resourcesPage.brandAssets')}</p>
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">{t('resourcesPage.logoAndFonts')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('resourcesPage.logoAndFontsDesc')}</p>
          </div>

          {/* Church Logo */}
          <div className="mb-16">
            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-8 text-center">{t('resourcesPage.churchLogo')}</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 border border-stone-200 text-center">
                <div className="w-40 h-40 mx-auto mb-6 flex items-center justify-center bg-white rounded-xl">
                  <img 
                    src="/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png" 
                    alt="Church Logo - Color"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{t('resourcesPage.fullColorLogo')}</h4>
                <p className="text-sm text-gray-500 mb-4">{t('resourcesPage.primaryUse')}</p>
                <a href="/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png" download="church-logo-full-color.png" className="inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line"></i>
                  {t('resourcesPage.downloadPNG')}
                </a>
              </div>

              <div className="bg-[#3d1a1e] rounded-2xl p-8 text-center">
                <div className="w-40 h-40 mx-auto mb-6 flex items-center justify-center rounded-xl">
                  <img 
                    src="/assets/readdy/534778143ab6f863545ce34e9f19d765-0edfb196bfba.png" 
                    alt="Church Logo - Light"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <h4 className="font-bold text-white mb-2">{t('resourcesPage.whiteLogo')}</h4>
                <p className="text-sm text-white/60 mb-4">{t('resourcesPage.darkBackgrounds')}</p>
                <a href="/assets/readdy/534778143ab6f863545ce34e9f19d765-0edfb196bfba.png" download="church-logo-white.png" className="inline-flex items-center gap-2 text-sm font-medium text-amber-400 hover:text-amber-300 cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line"></i>
                  {t('resourcesPage.downloadPNG')}
                </a>
              </div>

              <div className="bg-stone-100 rounded-2xl p-8 text-center">
                <div className="w-40 h-40 mx-auto mb-6 flex items-center justify-center rounded-xl">
                  <img 
                    src="/assets/readdy/bbac4cf6f1d46c3eca90cfbedff18e2e-0c15da9be055.png" 
                    alt="Church Logo - Monochrome"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{t('resourcesPage.monochromeLogo')}</h4>
                <p className="text-sm text-gray-500 mb-4">{t('resourcesPage.printMaterials')}</p>
                <a href="/assets/readdy/bbac4cf6f1d46c3eca90cfbedff18e2e-0c15da9be055.png" download="church-logo-monochrome.png" className="inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line"></i>
                  {t('resourcesPage.downloadPNG')}
                </a>
              </div>
            </div>

            {/* Icon Only Subtitle */}
            <h4 className="text-xl font-serif font-bold text-gray-900 mt-12 mb-6 text-center">{t('resourcesPage.iconOnly')}</h4>

            <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {/* Icon Only — Light (for dark backgrounds) */}
              <div className="bg-[#3d1a1e] rounded-2xl p-8 text-center">
              <div className="w-40 h-40 mx-auto mb-6 flex items-center justify-center rounded-xl">
                <img
                  src="/assets/readdy/59015c7862826d107cf1a11d0143dff2-ec628b9064b0.png"
                  alt="Church Logo Icon Only - Light"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h4 className="font-bold text-white mb-2">{t('resourcesPage.outlineLightLogo')}</h4>
              <p className="text-sm text-white/60 mb-4">{t('resourcesPage.outlineLightDesc')}</p>
              <a href="/assets/readdy/59015c7862826d107cf1a11d0143dff2-ec628b9064b0.png" download="church-logo-icon-light.png" className="inline-flex items-center gap-2 text-sm font-medium text-amber-400 hover:text-amber-300 cursor-pointer whitespace-nowrap">
                <i className="ri-download-line"></i>
                {t('resourcesPage.downloadPNG')}
              </a>
            </div>

            {/* Icon Only — Dark (for light backgrounds) */}
            <div className="bg-white rounded-2xl p-8 border border-stone-200 text-center">
              <div className="w-40 h-40 mx-auto mb-6 flex items-center justify-center bg-white rounded-xl">
                <img
                  src="/assets/readdy/199db0f5bc09613dc24b7935c1ef00f4-5c30a377ef6b.png"
                  alt="Church Logo Icon Only - Dark"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('resourcesPage.outlineDarkLogo')}</h4>
              <p className="text-sm text-gray-500 mb-4">{t('resourcesPage.outlineDarkDesc')}</p>
              <a href="/assets/readdy/199db0f5bc09613dc24b7935c1ef00f4-5c30a377ef6b.png" download="church-logo-icon-dark.png" className="inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap">
                <i className="ri-download-line"></i>
                {t('resourcesPage.downloadPNG')}
              </a>
            </div>
            </div>
          </div>

          {/* Coptic Font Download */}
          <div>
            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-8 text-center">{t('resourcesPage.copticFonts')}</h3>
            <div className="bg-white rounded-2xl p-8 border border-stone-200">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-600 rounded-xl">
                      <i className="ri-font-size-2 text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{t('resourcesPage.copticFontsTitle')}</h4>
                      <p className="text-sm text-gray-500">{t('resourcesPage.copticFontsSubtitle')}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">{t('resourcesPage.copticFontsDesc')}</p>
                  <div className="flex flex-wrap gap-3">
                    <a href="https://www.dropbox.com/scl/fi/459ek1qge79pu2qooy8e8/copticfonts.zip?rlkey=vo96eebekk6b7kpurf17p3u9l&st=ruaukoke&raw=1" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3d1a1e] text-white font-medium rounded-lg hover:bg-[#2a1215] transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-download-line"></i>
                      {t('resourcesPage.downloadFontPack')}
                    </a>
                    <button onClick={() => setShowInstallGuide(!showInstallGuide)} className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                      <i className={`${showInstallGuide ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                      {t('resourcesPage.installationGuide')}
                    </button>
                  </div>
                  {showInstallGuide && (
                    <div className="mt-6 bg-stone-50 rounded-xl p-6 border border-stone-200">
                      <h5 className="font-bold text-gray-900 mb-4">{t('resourcesPage.installationGuideTitle')}</h5>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg">
                              <i className="ri-windows-line"></i>
                            </div>
                            <span className="font-semibold text-gray-900">{t('resourcesPage.installationGuideWindows')}</span>
                          </div>
                          <ol className="space-y-3 text-sm text-gray-600">
                            <li className="flex gap-2">
                              <span className="font-bold text-amber-600 flex-shrink-0">1.</span>
                              <span>{t('resourcesPage.installationGuideStep1')}</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold text-amber-600 flex-shrink-0">2.</span>
                              <span>{t('resourcesPage.installationGuideStep2')}</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold text-amber-600 flex-shrink-0">3.</span>
                              <span>{t('resourcesPage.installationGuideStep3Win')}</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold text-amber-600 flex-shrink-0">4.</span>
                              <span>{t('resourcesPage.installationGuideStep4')}</span>
                            </li>
                          </ol>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg">
                              <i className="ri-apple-line"></i>
                            </div>
                            <span className="font-semibold text-gray-900">{t('resourcesPage.installationGuideMac')}</span>
                          </div>
                          <ol className="space-y-3 text-sm text-gray-600">
                            <li className="flex gap-2">
                              <span className="font-bold text-amber-600 flex-shrink-0">1.</span>
                              <span>{t('resourcesPage.installationGuideStep1')}</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold text-amber-600 flex-shrink-0">2.</span>
                              <span>{t('resourcesPage.installationGuideStep2')}</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold text-amber-600 flex-shrink-0">3.</span>
                              <span>{t('resourcesPage.installationGuideStep3Mac')}</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-bold text-amber-600 flex-shrink-0">4.</span>
                              <span>{t('resourcesPage.installationGuideStep4')}</span>
                            </li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bg-stone-50 rounded-xl p-6">
                  <p className="text-center text-3xl mb-4 font-serif" style={{ fontFamily: 'serif' }}>
                    ⲡⲓⲭⲣⲓⲥⲧⲟⲥ ⲁϥⲧⲱⲛϥ
                  </p>
                  <p className="text-center text-gray-500 text-sm">{t('resourcesPage.sampleText')}</p>
                  <div className="mt-4 pt-4 border-t border-stone-200">
                    <p className="text-xs text-gray-500 text-center">{t('resourcesPage.fontsIncluded')}</p>
                    <div className="flex flex-wrap justify-center gap-2 mt-2">
                      <span className="px-2 py-1 bg-white rounded text-xs text-gray-600">CS Avva Shenouda</span>
                      <span className="px-2 py-1 bg-white rounded text-xs text-gray-600">CS Pishoi</span>
                      <span className="px-2 py-1 bg-white rounded text-xs text-gray-600">CS Copt</span>
                      <span className="px-2 py-1 bg-white rounded text-xs text-gray-600">New Athanasius</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-amber-50 rounded-2xl p-6 border border-amber-200">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg flex-shrink-0">
                <i className="ri-information-line text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">{t('resourcesPage.usageGuidelines')}</h4>
                <p className="text-sm text-gray-600">{t('resourcesPage.usageGuidelinesDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">{t('resourcesPage.needHelp')}</h3>
          <p className="text-gray-600 mb-8">{t('resourcesPage.needHelpDesc')}</p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3 bg-[#3d1a1e] text-white font-medium rounded-full hover:bg-[#2a1215] transition-colors cursor-pointer whitespace-nowrap">
            {t('contactPage.title')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}
